/**
 * Um livro da lista compartilhada da casa (`our_books`).
 *
 * É o `OurBooksItem` de `lib/features/books/data/models/our_books_item.dart`.
 * Quem já leu mora em `lidoPor`, por slug: antes eram dois campos fixos,
 * `mohamedReadAt` e `isabelleReadAt`, e numa casa de três não havia onde
 * guardar a leitura da terceira pessoa.
 */

import { gravarData, lerData } from '../nucleo/tempo';
import type { Casa } from '../types/casa';
import { livroNovo, type Livro } from './livro';

export const COLECAO_NOSSOS_LIVROS = 'our_books';

export interface NossoLivro {
  id: string;
  obra: string;
  edicao: string;
  iaId: string;
  titulo: string;
  autor: string;
  capa: string;
  ano: string;
  assuntos: string[];
  /** Slug de quem colocou o livro na lista. */
  quemPos: string;
  quando: Date;
  /** Slug → quando essa pessoa terminou. Ausente é "ainda não leu". */
  lidoPor: Record<string, Date>;
}

export const leuPor = (livro: NossoLivro, slug?: string | null): boolean =>
  !!slug && slug in livro.lidoPor;

export const alguemLeu = (livro: NossoLivro): boolean =>
  Object.keys(livro.lidoPor).length > 0;

/**
 * `OurBooksItem.status`. `read-both` continua querendo dizer "todo mundo da
 * casa leu", e não "duas pessoas leram": é o valor que já está gravado.
 */
export function statusDoLivro(livro: NossoLivro, casa?: Casa | null): string {
  const lidos = Object.keys(livro.lidoPor);
  if (lidos.length === 0) return 'to-read';
  const daCasa = (casa?.pessoas ?? []).map((p) => p.slug);
  if (daCasa.length > 0 && daCasa.every((s) => s in livro.lidoPor)) return 'read-both';
  // A ordem da casa, e não a ordem em que o mapa foi montado, para o rótulo não
  // trocar de nome entre duas leituras do mesmo documento.
  const primeiro = daCasa.find((s) => s in livro.lidoPor) ?? (lidos[0] as string);
  return `read-${primeiro}`;
}

/** `OurBooksItem.statusLabel`. */
export function rotuloDoNossoLivro(livro: NossoLivro, casa?: Casa | null): string {
  const s = statusDoLivro(livro, casa);
  if (s === 'to-read') return 'Para ler juntos';
  if (s === 'read-both') return 'Todo mundo leu';
  const slug = s.slice('read-'.length);
  const pessoa = casa?.pessoas.find((p) => p.slug === slug);
  return `${pessoa?.nome ?? slug} leu`;
}

/** `OurBooksItem.readSourceLabel`, calculado na hora. */
export function rotuloDaFonteDoNossoLivro(livro: NossoLivro): string {
  if (livro.iaId) return 'Internet Archive';
  if (livro.obra) return 'Open Library';
  return '';
}

/**
 * Lê o mapa novo e, se ele não existir, os dois campos antigos: os documentos
 * gravados antes desta versão continuam abrindo.
 */
export function lidosDe(dados: Record<string, unknown>): Record<string, Date> {
  const saida: Record<string, Date> = {};
  const bruto = dados.lidoPor;
  if (bruto && typeof bruto === 'object' && !Array.isArray(bruto)) {
    for (const [slug, valor] of Object.entries(bruto as Record<string, unknown>)) {
      const quando = lerData(valor);
      if (quando) saida[slug] = quando;
    }
  }
  for (const [slug, campo] of [
    ['mohamed', 'mohamedReadAt'],
    ['isabelle', 'isabelleReadAt'],
  ] as const) {
    if (slug in saida) continue;
    const quando = lerData(dados[campo]);
    if (quando) saida[slug] = quando;
  }
  return saida;
}

export function nossoLivroDeMapa(id: string, dados: Record<string, unknown>): NossoLivro {
  const texto = (v: unknown): string => (typeof v === 'string' ? v : '');
  return {
    id,
    obra: texto(dados.workKey),
    edicao: texto(dados.editionKey),
    iaId: texto(dados.iaId),
    titulo: texto(dados.title),
    autor: texto(dados.author),
    capa: texto(dados.coverUrl),
    ano: texto(dados.year),
    assuntos: Array.isArray(dados.subjects) ? dados.subjects.map((a) => String(a)) : [],
    quemPos: texto(dados.addedBy),
    quando: lerData(dados.addedAt) ?? new Date(),
    lidoPor: lidosDe(dados),
  };
}

export function nossoLivroParaMapa(livro: Omit<NossoLivro, 'id'>): Record<string, unknown> {
  const lidos: Record<string, string> = {};
  for (const [slug, quando] of Object.entries(livro.lidoPor)) lidos[slug] = gravarData(quando);
  const mohamed = livro.lidoPor.mohamed;
  const isabelle = livro.lidoPor.isabelle;
  return {
    workKey: livro.obra,
    editionKey: livro.edicao,
    iaId: livro.iaId,
    title: livro.titulo,
    author: livro.autor,
    coverUrl: livro.capa,
    year: livro.ano,
    subjects: livro.assuntos,
    addedBy: livro.quemPos,
    addedAt: gravarData(livro.quando),
    lidoPor: lidos,
    // Os dois campos antigos continuam sendo escritos enquanto houver aparelho
    // com a versão anterior instalada: ela só sabe ler estes.
    mohamedReadAt: mohamed ? gravarData(mohamed) : null,
    isabelleReadAt: isabelle ? gravarData(isabelle) : null,
  };
}

/** `OurBooksItem.toBookItem`: o livro da casa visto como um livro de lista. */
export function nossoLivroParaLivro(livro: NossoLivro, casa?: Casa | null): Livro {
  return livroNovo({
    id: livro.id,
    obra: livro.obra,
    edicao: livro.edicao,
    iaId: livro.iaId,
    titulo: livro.titulo,
    autor: livro.autor,
    capa: livro.capa,
    ano: livro.ano,
    assuntos: livro.assuntos,
    status: statusDoLivro(livro, casa),
    quem: livro.quemPos,
    quando: livro.quando,
  });
}

/** Um livro da casa vazio, para quem monta um a partir de uma busca. */
export function nossoLivroNovo(campos: Partial<NossoLivro> = {}): NossoLivro {
  return {
    id: '',
    obra: '',
    edicao: '',
    iaId: '',
    titulo: '',
    autor: '',
    capa: '',
    ano: '',
    assuntos: [],
    quemPos: '',
    quando: new Date(),
    lidoPor: {},
    ...campos,
  };
}
